import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { toTitleCase } from 'src/app/services/util/utils';

type TenantFilter = 'all' | 'foundation' | 'external' | 'aws' | 'azure';

@Component({
  selector: 'app-tenant-select-dialog',
  templateUrl: './tenant-select-dialog.component.html',
  styleUrl: './tenant-select-dialog.component.scss',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, TranslocoModule]
})
export class TenantSelectDialogComponent {
  searchValue = '';
  activeFilter: TenantFilter = 'all';
  pendingId: string;

  filters: { key: TenantFilter; label: string }[] = [
    { key: 'all', label: 'TENANT_LAYOUT.FILTER_ALL' },
    { key: 'foundation', label: 'TENANT_LAYOUT.FILTER_FOUNDATION' },
    { key: 'external', label: 'TENANT_LAYOUT.FILTER_EXTERNAL' },
    { key: 'aws', label: 'TENANT_LIST.AWS' },
    { key: 'azure', label: 'TENANT_LIST.AZURE' }
  ];

  constructor(private ref: MatDialogRef<TenantSelectDialogComponent>,@Inject(MAT_DIALOG_DATA) public data: { tenants: any[]; selectedTenantId: string }) {
      this.pendingId = data.selectedTenantId;
  }

  get filteredTenants(): any[] {
    const q = this.searchValue.trim().toLowerCase();
    return (this.data.tenants || []).filter(t => {
      const matchesSearch = !q || t.tenantId?.toLowerCase().includes(q);
      return matchesSearch && this.matchesFilter(t);
    });
  }

  private matchesFilter(t: any): boolean {
    const cloud = (t.cloudProvider || t.targetCloud || '').toLowerCase();
    switch (this.activeFilter) {
      case 'foundation': return t.origin !== 'external';
      case 'external':   return t.origin === 'external';
      case 'aws':        return cloud === 'aws';
      case 'azure':      return cloud === 'azure';
      default:           return true;
    }
  }

  initials(t: any): string {
    const id: string = t.tenantId || '';
    return id.length <= 2 ? id.toUpperCase() : id.charAt(0).toUpperCase();
  }

  subtitle(t: any): string {
    return [
      toTitleCase(t.customerName) || '-',
      t.cloudProvider || t.targetCloud,
      t.origin === 'external' ? '2.0' : 'Foundation'
    ].filter(Boolean).join(' | ');
  }

  select(t: any): void { 
    this.pendingId = t.tenantId; 
  }

  get changed(): boolean { 
    return !!this.pendingId && this.pendingId !== this.data.selectedTenantId && this.filteredTenants.some(t => t.tenantId === this.pendingId); 
  }

  onSearch(value: string): void {
    this.searchValue = value;
  }

  setFilter(key: TenantFilter): void {
    this.activeFilter = key;
  }
  
  confirm(): void { 
    if (!this.changed) { 
      return; 
    }
    this.ref.close(this.pendingId); 
  }

  cancel(): void { 
    this.ref.close(); 
  }
}
